<script setup lang="ts">
import type { ICardCard } from '~/components/entity/card/item/types'
import type { IEntityPosition } from '~/components/entity/base-entity'
import { cardOptionsLabels, type CardOptionKey } from '~/components/entity/card/item/mocks'
import { Badge } from '~/components/ui/badge'
import { UiCardTop } from '~/components/ui/card'
import { LucideScale, LucideX } from 'lucide-vue-next'
import { Rating } from '~/components/ui/rating'

const props = withDefaults(
  defineProps<{
    item: ICardCard
    hideOrganizationDetails?: boolean
    hideCompare?: boolean
    position?: IEntityPosition
  }>(),
  {
    hideOrganizationDetails: false,
    hideCompare: false,
    position: undefined
  }
)

const { t } = useI18n()
const isHighlighted = useEntityHighlight(props.item, props.position)

const activeTypes = computed(() => {
  if (!props.item.type) return []
  return Object.keys(props.item.type).filter((key) => props.item.type[key])
})

const activeVariants = computed(() => {
  if (!props.item.system.variants) return []
  return Object.keys(props.item.system.variants).filter((key) => props.item.system.variants[key])
})

const activeOptions = computed(() => {
  const options: CardOptionKey[] = []
  Object.keys(cardOptionsLabels).forEach((key) => {
    const optionKey = key as CardOptionKey
    if (props.item[optionKey]) {
      options.push(optionKey)
    }
  })
  return options.slice(0, 5)
})

const currencyTypeLabel = computed(() =>
  props.item.currency_type === 'multi'
    ? t('entity.card.item.multi_currency')
    : t('entity.card.item.mono_currency')
)

const activeTypeLabels = computed(() =>
  activeTypes.value.map((el) => (el === 'debit' ? t('common.debit') : t('common.credit_card')))
)

const compareStore = useCompareStore()
const isInCompare = computed(() => compareStore.has('cards', props.item.id))
const atLimit = computed(() => compareStore.countByType.cards >= 10)

const toggleCompare = () => {
  if (isInCompare.value) {
    compareStore.remove('cards', props.item.id)
  } else {
    compareStore.add('cards', props.item.id)
  }
}
</script>

<template>
  <article :class="[isHighlighted ? 'CardPremium group' : 'Card']">
    <UiCardTop v-if="isHighlighted" />
    <div class="EntityCardLayout">
      <div class="flex gap-4">
        <NuxtLinkLocale
          :to="props.item.url"
          :aria-label="`${$t('entity.card.item.more_about_card')} ${props.item.title}`"
        >
          <div class="relative">
            <NuxtImg
              :src="props.item.logo"
              :alt="props.item.title"
              class="w-24 min-w-24 rounded-md object-contain md:w-28 md:min-w-28"
            />
            <NuxtImg
              v-if="isHighlighted"
              src="/images/photostock/brilliant.png"
              width="24"
              height="24"
              alt="premium marker"
              class="absolute bottom-[-6px] right-[-6px]"
              format="avif,webp"
            />
          </div>
        </NuxtLinkLocale>
        <div class="flex flex-col items-start">
          <NuxtLinkLocale
            :to="props.item.url"
            :aria-label="
              t('entity.card.item.more_about_card_from', {
                title: props.item.title,
                org: props.item.organization.title
              })
            "
            class="inline-block hover:underline"
          >
            <Heading tag="h3" class="hit-title h6"><span v-html="props.item.title" /></Heading>
          </NuxtLinkLocale>
          <p class="SecondaryTextHeadings">
            {{ currencyTypeLabel }}
            {{ props.item.system.title }}
            <span v-if="activeVariants.length" class="capitalize">
              &mdash; {{ activeVariants.join(', ') }}
            </span>
          </p>
          <NuxtLinkLocale
            v-if="!props.hideOrganizationDetails"
            :to="props.item.organization.url"
            :aria-label="
              t('entity.card.item.more_about_org', { org: props.item.organization.title })
            "
            class="CardOrgLink"
          >
            {{ props.item.organization.title }}
          </NuxtLinkLocale>
          <div v-if="props.item.ratings" class="flex items-center">
            <Rating :value="Number(props.item.ratings)" size="xs" />
            <div class="ml-2 text-sm text-gray-900">{{ props.item.ratings }}</div>
          </div>
        </div>
      </div>
      <div class="grid grid-cols-3 gap-y-2">
        <div>
          <div class="SecondaryTextHeadings">{{ $t('entity.card.item.annual_fee') }}</div>
          <div v-if="props.item.service_fee === 0" class="font-semibold">
            {{ $t('entity.card.item.free') }}
          </div>
          <div v-else class="font-semibold">
            {{ useCurrencyFormat(props.item.service_fee, 1) }}
          </div>
        </div>
        <div v-if="activeTypes.length">
          <div class="SecondaryTextHeadings">{{ $t('common.type') }}</div>
          <div class="font-semibold">{{ activeTypeLabels.join(', ') }}</div>
        </div>
        <div v-if="props.item.currencies && props.item.currencies.length">
          <div class="SecondaryTextHeadings">{{ $t('common.currency') }}</div>
          <div class="font-semibold">
            <span>{{ props.item.currencies.map((i) => i.code).join(', ') }}</span>
          </div>
        </div>
      </div>
    </div>
    <footer :class="[{ 'gap-2': activeOptions.length }, 'mt-4 flex flex-wrap items-start']">
      <div class="flex flex-1 flex-wrap gap-2">
        <template v-if="activeOptions.length">
          <Badge
            v-for="option in activeOptions"
            :key="option"
            variant="gray"
            class="text-xs"
            rounded="full"
          >
            {{ $t(cardOptionsLabels[option]) }}
          </Badge>
        </template>
      </div>
      <div v-if="!hideCompare" class="flex w-full justify-end sm:-mt-1 sm:w-auto">
        <Button
          variant="ghost"
          size="sm"
          :disabled="!isInCompare && atLimit"
          class="CompareButton"
          @click="toggleCompare"
        >
          <LucideX v-show="isInCompare" class="size-3.5" />
          <LucideScale v-show="!isInCompare" class="size-3.5" />
          {{ isInCompare ? t('compare.remove') : t('compare.add') }}
        </Button>
      </div>
    </footer>
  </article>
</template>
